import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ContPlan, Messages, Role } from '@/common/constants';
import Loading from '@/components/common/Loading';
import URL from '@/common/routes/Url';
import CommonActionButton from '@/components/common/CommonActionButton';
import Tenant from '@/types/Tenant.type';
import { Prefecture, Prefectures } from '@/types/Prefecture.type';
import PrefectureService from '@/services/Prefecture.service';
import TenantService from '@/services/Tenant.service';

type TenantForm = Tenant & Prefecture

const TenantDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<TenantForm>({ shouldUnregister: false });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    getValues,
  } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [prefecture, setPrefecture] = useState<Prefecture[] | []>([]);
  const [loading, setLoading] = useState(true);

  const inputs: Array<HookFormControlsProps> = [
    {
      name: 'name',
      type: 'text',
      label: '会社名',
      rule: {
        required: '会社名は必須項目です。',
        maxLength: {
          value: 255,
          message: '会社名は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'zipCode',
      type: 'text',
      label: '郵便番号',
      rule: {
        required: '郵便番号は必須項目です。',
        pattern: {
          value: new RegExp('^([0-9]{7})$'),
          message: '郵便番号の形式が不正です。',
        },
      },
    },
    {
      name: 'prefecture',
      type: 'autocomplete',
      label: '都道府県',
      options: prefecture,
      getOptionLabel: 'name',
      rule: {
        required: '都道府県は必須項目です。'
      },
    },
    {
      name: 'address1',
      type: 'text',
      label: '住所1',
      rule: {
        required: '住所1は必須項目です。',
        maxLength: {
          value: 255,
          message: '住所1は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'address2',
      type: 'text',
      label: '住所2',
      rule: {
        maxLength: {
          value: 255,
          message: '住所2は255字以内で入力してください。',
        },
      },
    },
    {
      name: 'phoneNumber',
      type: 'text',
      label: '電話番号',
      rule: {
        required: '電話番号は必須項目です。',
        maxLength: {
          value: 15,
          message: '電話番号は15字以内で入力してください。',
        },
      },
    },
    {
      name: 'adminName',
      type: 'text',
      label: 'システム管理者名',
      rule: {
        required: 'システム管理者名は必須項目です。',
        maxLength: {
          value: 15,
          message: 'システム管理者名は15字以内で入力してください。',
        },
      },
    },
    {
      name: 'adminMail',
      type: 'text',
      label: 'メールアドレス',
      rule: {
        required: 'メールアドレスは必須項目です。',
        maxLength: {
          value: 255,
          message: 'メールアドレスは255文字以内で入力してください。',
        },
        pattern: {
          value: new RegExp(
            "^[a-zA-Z0-9\\.\\!\\#\\$\\%\\&\\'\\*\\+\\/\\=\\\\\\?\\^\\_\\`\\{\\|\\}\\~\\-]+\\@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
          ),
          message: 'メールアドレスの形式が不正です。',
        },
      },
    },
    {
      name: 'adminDepartment',
      type: 'text',
      label: '所属部署',
      rule: {
        required: '所属部署は必須項目です。',
        maxLength: {
          value: 15,
          message: '所属部署は15字以内で入力してください。',
        },
      },
    },
    {
      name: 'startDate',
      type: 'date',
      label: '利用開始日',
      rule: {
        required: '契約プランは必須項目です。',
      },
    },
    {
      name: 'endDate',
      type: 'date',
      label: '利用終了日',
    },
    {
      name: 'contPlan',
      type: 'select',
      label: '契約プラン',
      options: ContPlan,
      rule: {
        required: '契約プランは必須項目です。',
      },
    },
    {
      name: 'maxUsers',
      type: 'number',
      label: '利用人数',
      rule: {
        required: '利用人数は必須項目です。',
        // max: { value: 9999, message: '利用人数は9,999以内で入力してください。' },
        // min: { value: 30, message: '利用人数は10以上で入力してください。' },
      },
    },

  ];

  useLayoutEffect(() => {
    (async () => {
      await PrefectureService.getAll().then((response: { data: Prefecture[] }) => {
        setPrefecture(response.data);
      });
      if (!pathParams.id) {
        setLoading(false);
        return;
      }
      await TenantService.get(pathParams.id)
        .then((response: { data: Tenant & Prefecture }) => {
          response.data.phoneNumber = response.data.phoneNumber ?? '';
          reset(response.data);
        })
        .catch((_e) => {
          setLoading(false);
          enqueueSnackbar(Messages.select.failure);
        });
      setLoading(false);
    })();
  }, []);

  const onSubmit = async (values: any) => {
    try {
      const request: Tenant = { ...values, ...{ prefecturesCd: values.prefecture ? values.prefecture.cd : null } };

      if (pathParams.id) {
        await TenantService.update(request, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await TenantService.create(request);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      navigate(URL.tenants);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8}>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>テナント管理 ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <p>テナント管理</p>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={5}>
                  <Grid container spacing={3}>
                    <Grid item key={0} xs={12}>
                      <HookFormControls key={0} {...inputs[0]} />
                    </Grid>
                    <Grid item key={1} xs={6}>
                      <HookFormControls key={1} {...inputs[1]} />
                    </Grid>
                    <Grid item key={2} xs={6}>
                      <HookFormControls key={2} {...inputs[2]} />
                    </Grid>
                    {inputs.map((input, index) => (
                      index > 2 && index < 6 &&
                      <Grid item key={index} xs={12}>
                        <HookFormControls key={index} {...input} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={5}>
                  <Grid container spacing={3}>
                    {inputs.map((input, index) => (
                      index > 5 && index < 9 &&
                      <Grid item key={index} xs={12}>
                        <HookFormControls key={index} {...input} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={2}>
                  <Grid container spacing={3}>
                    {inputs.map((input, index) => (
                      index > 8 &&
                      <Grid item key={index} xs={12}>
                        <HookFormControls key={index} {...input} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default TenantDetail;
